import client from './client';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { sendLog, LogLevel } from '@/api/client-logs';
import store from '@/store';
import { ApiErrorClass, apiErrorList } from '@/resources/error/apiErrorList';
import ApiCancelError from '@/resources/error/ApiCancelError';
import ApiModalError from '@/resources/error/ApiModalError';
import { StateApiError } from '@/store/modules/error';
import { ErrorCode } from '@/resources/error/ErrorCode';

// 通信テストなどのためにユーザー操作が必要になるエラーハンドリングを停止できるようにする
let isApiErrorHandling: boolean = true;
export function stopClientApiErrorHandling() {
  isApiErrorHandling = false;
}

// 通信処理を拡張してエラーハンドリングを追加する
const template = async <T>(request: () => Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> => {
  if (!isApiErrorHandling) return await request();

  const waitTimeList: number[] = [500, 2000];
  const loop = async (): Promise<AxiosResponse<T>> => {
    const prevApiCancelCount: number = store.getters['error/apiCancelCount'];

    try {
      const response: AxiosResponse<T> = await request();

      // キャンセル確認
      const nextApiCancelCount: number = store.getters['error/apiCancelCount'];
      if (prevApiCancelCount !== nextApiCancelCount) throw new ApiCancelError('');

      return response;
    } catch (error) {
      // キャンセル確認
      if (error instanceof ApiCancelError) return Promise.reject(error);
      const nextApiCancelCount: number = store.getters['error/apiCancelCount'];
      if (prevApiCancelCount !== nextApiCancelCount) return Promise.reject(new ApiCancelError(''));

      if (axios.isAxiosError(error) && error.response) {
        // サーバからエラーが返された
        const status: number = error.response.status;
        const code: ErrorCode = (error.response.data && error.response.data.error_code) || '-1';

        // メンテナンス確認
        if (status === 503 && code === ErrorCode.Maintenance) {
          const isFields: boolean = error.response.data.fields && Array.isArray(error.response.data.fields);
          store.commit('maintenance/setMaintenance', true);
          store.commit('maintenance/setStart', { date: isFields ? error.response.data.fields[0] : '' });
          store.commit('maintenance/setEnd', { date: isFields ? error.response.data.fields[1] : '' });
          store.commit('maintenance/setTitle', { title: isFields ? error.response.data.fields[2] : '' });
          store.commit('maintenance/setMessage', { message: isFields ? error.response.data.fields[3] : '' });
          return Promise.reject(new ApiCancelError(''));
        }

        // // 通信失敗ログの送信
        // const level: LogLevel = status >= 500 ? 'ERROR' : 'INFO';
        // const config: AxiosRequestConfig | undefined = error.response.config;
        // const api: string | undefined = config && config.url && config.url.replace(config.baseURL || '', '');
        // const message: string = `${error.message} ${api} ${status} ${code}`;
        // sendLog({ level, kind: 'request_failed', message });

        // エラーコードからエラー分類
        const apiError = apiErrorList.find((apiError: ApiErrorClass): boolean => {
          const isStatus: boolean = apiError.status === status || apiError.status < 0;
          const isCode: boolean = apiError.code === code;
          return isStatus && isCode;
        });

        const i18nDefaultTitle: string = 'Root.modalDefaultTitleError';
        let i18nDefaultText: string = 'error.DefaultErrorMessage';
        if (process.env.NODE_ENV === 'development') {
          i18nDefaultText = `${i18nDefaultText} (${status}, ${code})`;
        }

        // エラー分類の足りない情報をデフォルト値で埋める
        const type = (apiError && apiError.type) || (status === 401 ? 'redirect' : 'warn');
        const i18nTitle: string = (apiError && apiError.i18nTitle) || i18nDefaultTitle;
        const i18nText: string = (apiError && apiError.i18nText) || i18nDefaultText;
        const isModal: boolean = apiError && apiError.isModal !== undefined ? apiError.isModal : true;

        if (!isModal) return Promise.reject(error);
        const isRetry = await new Promise<boolean>((resolve: (isRetry: boolean) => void): void => {
          const stateApiError: StateApiError = { type, i18nTitle, i18nText, code, next: resolve };
          store.dispatch('error/addApiError', stateApiError);
        });
        if (isRetry) return await loop();
        return Promise.reject(new ApiModalError(''));
      } else if (waitTimeList.length > 0) {
        // サーバから何も返ってこなかったら少し待ってリトライする
        const waitTime: number = waitTimeList.shift()!;
        await new Promise<void>((resolve) => window.setTimeout(resolve, waitTime));
        return await loop();
      } else {
        // 通信失敗ログの送信
        const level: LogLevel = 'INFO';
        const config: AxiosRequestConfig | undefined = axios.isAxiosError(error) ? error.config : undefined;
        const api: string | undefined = config && config.url && config.url.replace(config.baseURL || '', '');
        const message: string = `${error instanceof Error ? error.message : ''} ${api}`;
        sendLog({ level, kind: 'request_timeout', message });

        // リトライしてもダメならば通信エラー
        const type: 'retry' = 'retry';
        const i18nTitle: string = 'error.ConnectionShouldRetryTitle';
        const i18nText: string = 'error.ConnectionShouldRetryText';
        const code: ErrorCode = ErrorCode.Unknown;
        const isRetry = await new Promise<boolean>((resolve: (isRetry: boolean) => void): void => {
          const stateApiError: StateApiError = { type, i18nTitle, i18nText, code, next: resolve };
          store.dispatch('error/addApiError', stateApiError);
        });
        if (isRetry) return await loop();
        return Promise.reject(new ApiModalError(''));
      }
    }
  };
  return await loop();
};

export default {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    await template<T>(() => client.get<T>(url, config)),

  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    await template<T>(() => client.post<T>(url, data, config)),

  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    await template<T>(() => client.put<T>(url, data, config)),

  delete: async (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<void>> =>
    await template<void>(() => client.delete(url, config))
};
